import axios from "axios";

function getFemaleEnglishVoices() {
  return window.speechSynthesis
    .getVoices()
    .filter(
      (voice) =>
        voice.lang.toLowerCase().startsWith("en") &&
        /(female|woman|girl)/i.test(voice.name)
    );
}

// Handle delayed loading of voices
function loadVoices(callback) {
  let voices = window.speechSynthesis.getVoices();
  if (voices.length !== 0) {
    callback(getFemaleEnglishVoices());
  } else {
    window.speechSynthesis.onvoiceschanged = () => {
      callback(getFemaleEnglishVoices());
    };
  }
}

export const playSpeech = (text) => {
  if (!window.speechSynthesis) {
    console.warn("Speech synthesis not supported in this browser");
    return;
  }
  loadVoices((femaleVoices) => {
    // console.log("Female English Voices:", femaleVoices);
  });

  // 🧹 Cancel any ongoing speech before starting new
  window.speechSynthesis.cancel();

  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = "en-IN"; // 🇮🇳 Indian English
  utterance.rate = 0.9; // Slightly slower for clarity
  utterance.pitch = 1.2; // Gentle female tone
  utterance.volume = 1;

  const findVoice = () => {
    const voices = window.speechSynthesis.getVoices();

    if (!voices || voices.length === 0) {
      return setTimeout(findVoice, 100);
    }

    const voice =
      voices.find((v) => v.name.includes("Microsoft Zira")) ||
      voices.find((v) => v.name.includes("Google UK English Female")) ||
      voices.find((v) => v.lang.startsWith("en"));

    if (voice) {
      utterance.voice = voice;
    }

    window.speechSynthesis.speak(utterance);
  };

  // Some browsers require waiting for voices to load
  if (window.speechSynthesis.getVoices().length === 0) {
    window.speechSynthesis.onvoiceschanged = findVoice;
  } else {
    findVoice();
  }
};

// 🛑 Helper: stop speech instantly (call when popup closes)
export const stopSpeech = () => {
  if (window.speechSynthesis && window.speechSynthesis.speaking) {
    window.speechSynthesis.cancel();
    // console.log("🛑 Speech stopped");
  }
};

let currentAudio = null; // 👈 shared audio reference

export const playSpeechTTS = async (text, setCommonError) => {
  const ttsurl =
    "https://api.elevenlabs.io/v1/text-to-speech/EXAVITQu4vr4xnSDxMaL?output_format=mp3_44100_128";
  const apiKey = process.env.REACT_APP_ELEVENLABS_API_KEY;

  try {
    // 🛑 Stop any existing audio before starting new one
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      currentAudio = null;
    }

    const response = await axios.post(
      ttsurl,
      {
        text:
          text ||
          "Hello there! This is a full ElevenLabs TTS test with all options.",
        model_id: "eleven_multilingual_v2",
        voice_settings: {
          stability: 1,
          similarity_boost: 1,
          style: 1,
          use_speaker_boost: true,
          speaking_rate: 0.8, // slower pronunciation
          pitch: 1.0,
        },
        optimize_streaming_latency: 3,
        output_format: "mp3_44100_128",
        alignment: false,
        seed: 123,
        previous_text: "",
        next_text: "",
      },
      {
        headers: {
          "Content-Type": "application/json",
          "xi-api-key": apiKey,
          Accept: "audio/mpeg",
        },
        responseType: "arraybuffer",
      }
    );

    // 🎵 Convert binary audio to a Blob and assign globally
    const audioBlob = new Blob([response.data], { type: "audio/mpeg" });
    const audioUrl = URL.createObjectURL(audioBlob);

    currentAudio = new Audio(audioUrl); // 👈 assign here, not to a local const
    currentAudio.play();

    // cleanup once finished
    currentAudio.onended = () => {
      URL.revokeObjectURL(audioUrl);
      currentAudio = null;
    };
  } catch (error) {
    let message = "An unknown TTS error occurred.";
    let parsedData = null;

    if (error.response) {
      const { status, data } = error.response;

      // 🔍 Decode ArrayBuffer responses from ElevenLabs
      if (data instanceof ArrayBuffer) {
        try {
          const text = new TextDecoder("utf-8").decode(data);
          parsedData = JSON.parse(text);
        } catch (decodeError) {
          console.error("TTS Error: Failed to parse ArrayBuffer:", decodeError);
        }
      } else {
        parsedData = data;
      }

      // Extract meaningful message
      message = parsedData?.detail?.message || `TTS API error (HTTP ${status})`;

      console.error("🔴 TTS API Error:", {
        httpStatus: status,
        response: parsedData,
      });
    } else if (error.request) {
      message = "TTS network error — no response from ElevenLabs.";
      console.error("🌐 TTS Network Error:", error.request);
    } else {
      message = error.message;
      console.error("⚠️ TTS Unknown Error:", error.message);
    }
    console.error("❗ TTS Error Message:", message);
    setCommonError(message);
  }
};

export const stopSpeechTTS = () => {
  if (currentAudio) {
    currentAudio.pause();
    currentAudio.currentTime = 0;
    console.log("🛑 ElevenLabs TTS stopped");
    currentAudio = null;
  }
};
